import React, { useState, useEffect, useCallback } from 'react';
import API from '../../api/API';
import './ASquadreStoriche.css';

/**
 * Le squadre nel tempo, e a quale di esse appartiene ciascuna iscrizione.
 *
 * Una squadra e' una riga per edizione col nome a testo libero: senza questo aggancio le
 * statistiche cross-torneo non hanno modo di sapere che tre iscrizioni in tre anni sono la
 * stessa squadra. Chi non e' agganciato a una storica non compare in quella sezione: e' voluto,
 * non un bug da correggere qui.
 *
 * La scrittura ha una primitiva sola: assegnare una iscrizione a una storica, o a nessuna.
 * Ricongiungere due squadre e' spostare le iscrizioni dell'una sull'altra e poi eliminare la riga
 * rimasta vuota -- non c'e' un pulsante "fondi", perche' sarebbe una seconda strada per lo stesso
 * effetto, e una primitiva sola vuol dire un comportamento solo.
 */
const ASquadreStoriche = () => {
  const [storiche, setStoriche] = useState([]);
  const [edizioni, setEdizioni] = useState([]);
  const [edizione, setEdizione] = useState('');
  const [iscrizioni, setIscrizioni] = useState([]);
  const [nuovoNome, setNuovoNome] = useState('');
  const [loading, setLoading] = useState(true);
  const [seminando, setSeminando] = useState(false);

  const caricaStoriche = useCallback(async () => {
    const res = await API.squadraStorica.getAll();
    if (res.isSuccess) setStoriche(res.data || []);
  }, []);

  const caricaIscrizioni = useCallback(async (idTorneo) => {
    if (!idTorneo) { setIscrizioni([]); return; }
    const res = await API.squadraStorica.iscrizioni(idTorneo);
    if (res.isSuccess) setIscrizioni(res.data || []);
  }, []);

  useEffect(() => {
    (async () => {
      // Elenco delle edizioni che l'utente puo' leggere: qui espone solo id e data (nessun nome
      // di torneo o luogo), quindi la tendina sotto etichetta le voci con la sola data.
      const tornei = await API.torneo.getAll();
      if (tornei.isSuccess) setEdizioni(tornei.data || []);
      await caricaStoriche();
      setLoading(false);
    })();
  }, [caricaStoriche]);

  useEffect(() => {
    caricaIscrizioni(edizione);
  }, [edizione, caricaIscrizioni]);

  const handleAssegna = async (idSquadra, valore) => {
    const idStorica = valore === '' ? null : Number(valore);
    const res = await API.squadraStorica.assegna(idSquadra, idStorica);
    if (!res.isSuccess) {
      alert(res.errorMsg || 'Assegnazione non riuscita');
      // La <select> e' controllata, ma il browser ha gia' spostato il valore visibile prima che
      // questo handler finisse: senza questa ricarica la riga resterebbe a mostrare un aggancio
      // che sul server non e' mai avvenuto. Non e' ridondante con la ricarica del ramo di successo.
      await caricaIscrizioni(edizione);
      return;
    }
    await caricaIscrizioni(edizione);
    await caricaStoriche();
  };

  const handleCrea = async () => {
    if (!nuovoNome.trim()) return;
    const res = await API.squadraStorica.create({ nome: nuovoNome.trim() });
    if (!res.isSuccess) { alert(res.errorMsg || 'Creazione non riuscita'); return; }
    setNuovoNome('');
    await caricaStoriche();
  };

  const handleRinomina = async (riga) => {
    const nome = window.prompt('Nome della squadra', riga.nome);
    if (nome === null) return;
    const note = window.prompt('Perche\' queste iscrizioni sono la stessa squadra', riga.note ?? '');
    if (note === null) return;
    const res = await API.squadraStorica.update(riga.id, { nome, note });
    if (!res.isSuccess) { alert(res.errorMsg || 'Modifica non riuscita'); return; }
    await caricaStoriche();
    await caricaIscrizioni(edizione);
  };

  const handleElimina = async (riga) => {
    if (!window.confirm(`Eliminare "${riga.nome}"?`)) return;
    const res = await API.squadraStorica.delete(riga.id);
    if (!res.isSuccess) { alert(res.errorMsg || 'Eliminazione non riuscita'); return; }
    await caricaStoriche();
  };

  const handleSemina = async () => {
    if (!window.confirm('Creare una squadra storica per ogni nome presente in due o piu\' edizioni? Si puo\' rilanciare senza danni: non tocca chi e\' gia\' agganciato altrove.')) return;
    setSeminando(true);
    const res = await API.squadraStorica.semina();
    setSeminando(false);
    if (!res.isSuccess) { alert(res.errorMsg || 'Seed non riuscito'); return; }
    await caricaStoriche();
    await caricaIscrizioni(edizione);
    // E' l'unico riscontro che l'operatore ha del risultato: senza, un seed che non trova niente
    // di nuovo da creare e uno che ne crea dieci sembrano la stessa identica cosa.
    const create = res.data?.storicheCreate ?? 0;
    alert(create > 0 ? `Create ${create} nuove squadre storiche.` : 'Nessuna nuova squadra storica da creare.');
  };

  const formattaData = (data) => data ? new Date(data).toLocaleDateString('it-IT') : 'N/D';

  if (loading) {
    return (
      <div className="storiche-container">
        <div className="storiche-loading">
          <div className="storiche-spinner"></div>
          <p>Caricamento in corso...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="storiche-container">
      <div className="storiche-header">
        <div className="storiche-title">
          <i className="fas fa-clock-rotate-left"></i>
          <h1>Squadre storiche</h1>
        </div>
        <button
          type="button"
          className="storiche-btn storiche-btn-secondary"
          onClick={handleSemina}
          disabled={seminando}
        >
          <i className="fas fa-seedling"></i>
          {seminando ? 'Semina in corso...' : 'Semina dai nomi che ricorrono'}
        </button>
      </div>
      <p className="storiche-intro">
        La squadra nel tempo: cio' che lega le iscrizioni di edizioni diverse allo stesso nome.
        Chi non e' agganciato a una di queste righe non compare nelle statistiche cross-torneo.
      </p>

      <section className="storiche-sezione">
        <h2>Le storiche</h2>
        <div className="storiche-crea">
          <input
            type="text"
            value={nuovoNome}
            placeholder="Nome di una nuova squadra storica"
            onChange={(e) => setNuovoNome(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCrea()}
          />
          <button type="button" className="storiche-btn storiche-btn-primary" onClick={handleCrea} disabled={!nuovoNome.trim()}>
            <i className="fas fa-plus"></i> Crea
          </button>
        </div>
        {storiche.length > 0 ? (
          <div className="storiche-table-container">
            <table className="storiche-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Codice</th>
                  <th>Edizioni</th>
                  <th>Note</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {storiche.map((s) => (
                  <tr key={s.id}>
                    <td><strong>{s.nome}</strong></td>
                    <td><code>{s.codice}</code></td>
                    <td>{s.edizioni}</td>
                    <td>{s.note}</td>
                    <td>
                      <button type="button" className="storiche-btn storiche-btn-sm" onClick={() => handleRinomina(s)} title="Modifica nome o note">
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        type="button"
                        className="storiche-btn storiche-btn-delete storiche-btn-sm"
                        onClick={() => handleElimina(s)}
                        disabled={s.edizioni > 0}
                        title={s.edizioni > 0 ? 'Ha ancora iscrizioni agganciate: sganciale prima di eliminarla' : 'Elimina'}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="storiche-empty">
            <i className="fas fa-info-circle"></i>
            <p>Nessuna squadra storica presente. Creala a mano, o semina dai nomi che ricorrono.</p>
          </div>
        )}
      </section>

      <section className="storiche-sezione">
        <h2>Le iscrizioni</h2>
        <select value={edizione} onChange={(e) => setEdizione(e.target.value)}>
          <option value="">Scegli una edizione</option>
          {edizioni.map((t) => (
            <option key={t.id} value={t.id}>Edizione del {formattaData(t.date)}</option>
          ))}
        </select>
        {edizione && (
          iscrizioni.length > 0 ? (
            <div className="storiche-table-container">
              <table className="storiche-table">
                <thead>
                  <tr>
                    <th>Nome iscritto</th>
                    <th>Squadra storica</th>
                  </tr>
                </thead>
                <tbody>
                  {iscrizioni.map((i) => (
                    <tr key={i.idSquadra} className={i.idStorica ? '' : 'storiche-orfana'}>
                      <td>{i.nome}</td>
                      <td>
                        <select
                          value={i.idStorica ?? ''}
                          onChange={(e) => handleAssegna(i.idSquadra, e.target.value)}
                          aria-label={`Squadra storica per ${i.nome}`}
                        >
                          <option value="">— nessuna —</option>
                          {storiche.map((s) => (
                            <option key={s.id} value={s.id}>{s.nome}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="storiche-empty">
              <i className="fas fa-info-circle"></i>
              <p>Nessuna squadra iscritta a questa edizione.</p>
            </div>
          )
        )}
      </section>
    </div>
  );
};

export default ASquadreStoriche;
